import type { Contract } from '@gmn-portal/backend';
import { Activity, FileSignature, FileText, Tag, Users } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useContract, useCreateContract, useDeleteContract, useUpdateContract } from '@/core/api';
import type { PayloadApiError } from '@/core/api/client';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { DetailPageHeader, PageContainer, TabNav } from '@/shared/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { ContractActivity } from '../components/contract-activity';
import { ContractForm } from '../components/contract-form';
import type { ContractFormData } from '../types/contract-form';

export function ContractDetails() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  const { data: currentContract } = useContract(id!, { disabled: !isEditMode, depth: 2 });

  const [error, setError] = useState<PayloadApiError | undefined>();
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);

  const isGmnAdmin = user?.role === 'gmn-admin';
  const basePath = '/gmn/contract-management/contracts';

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const deleteContract = useDeleteContract();

  const currentTab = location.pathname.split('/').pop();

  const tabs = [
    { value: 'general', label: t('common.general'), icon: FileText },
    { value: 'activity', label: t('common.activity'), icon: Activity },
  ];

  const showActions = !isEditMode || currentTab === 'general';

  const submitForm = () => {
    const formElement = document.getElementById('contract-form') as HTMLFormElement | null;
    formElement?.requestSubmit();
  };

  const handleSubmit = async (data: ContractFormData) => {
    try {
      setError(undefined);
      if (isEditMode) {
        if (!isGmnAdmin) {
          return;
        }
        await updateContract.mutateAsync({ id: currentContract!.id, data });
      } else {
        if (!isGmnAdmin) {
          return;
        }
        const response = await createContract.mutateAsync({
          ...data,
        });
        navigate(`${basePath}/${response.doc.id}`);
      }
      toast.success(t('common.savedSuccessfully'));
    } catch (err) {
      setError(err as PayloadApiError);
      toast.error(t('common.errorSaving'));
    }
  };

  const handleDeleteContract = async () => {
    if (contractToDelete) {
      try {
        await deleteContract.mutateAsync(contractToDelete.id);
        toast.success(t('contracts.contractDeletedSuccessfully'));
        setContractToDelete(null);
      } catch (_error) {
        toast.error(t('contracts.errorDeletingContract'));
      }
    }
  };

  if (isEditMode && !currentContract) {
    return null;
  }

  return (
    <PageContainer animation="slide-right">
      <DetailPageHeader
        backHref={basePath}
        backLabel={t('contracts.backToOverview')}
        title={
          isEditMode && currentContract
            ? currentContract.title ||
              currentContract.contractNumber ||
              t('contracts.contractDetails')
            : t('contracts.newContract')
        }
        icon={FileSignature}
        isCreateMode={!isEditMode}
        metaItems={
          isEditMode && currentContract
            ? [
                ...(currentContract.contractNumber
                  ? [{ icon: Tag, label: currentContract.contractNumber }]
                  : []),
                ...(currentContract.customer &&
                typeof currentContract.customer === 'object' &&
                currentContract.customer.name
                  ? [{ icon: Users, label: currentContract.customer.name }]
                  : []),
              ]
            : undefined
        }
        subtitle={!isEditMode ? t('contracts.createNewContractHint') : undefined}
        secondaryAction={
          showActions
            ? {
                label: t('common.discard'),
                onClick: () => navigate(basePath),
              }
            : undefined
        }
        primaryAction={
          showActions
            ? {
                label: (isEditMode ? updateContract.isPending : createContract.isPending)
                  ? isEditMode
                    ? t('common.saving')
                    : t('common.creating')
                  : isEditMode
                    ? t('common.save')
                    : t('common.create'),
                onClick: submitForm,
                disabled:
                  !isGmnAdmin || (isEditMode ? updateContract.isPending : createContract.isPending),
                loading: isEditMode ? updateContract.isPending : createContract.isPending,
              }
            : undefined
        }
      />

      {isEditMode && <TabNav tabs={tabs} basePath={`${basePath}/${id}`} variant="underline" />}

      <div className="grid gap-6">
        {(!isEditMode || currentTab === 'general') && (
          <ContractForm
            id="contract-form"
            defaultValues={currentContract}
            onSubmit={handleSubmit}
            error={error}
          />
        )}

        {isEditMode && currentTab === 'activity' && <ContractActivity contractId={id} />}
      </div>

      <AlertDialog
        open={!!contractToDelete}
        onOpenChange={(open) => !open && setContractToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contracts.deleteContract')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contracts.deleteContractConfirmation', {
                name: contractToDelete?.contractNumber || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContract}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
